import React, { FC, useState } from 'react';

import { TopicBasicFragment } from '../../../../generated/graphql';
import TopicTemplateSelection from '../../../MUI/Compose/TopicTemplateSelection';
import Popover, { ExternalPopoverProps } from '../../../MUI/Popover';

import ComposeTopicTrigger from './ComposeTopicTrigger';

type Props = {
  ariaDescribedBy: string;
  ariaLabelledBy: string;
  dataTopicAll: TopicBasicFragment[];
  dataTopicSaved: TopicBasicFragment['id'] | null;
  dataSearchValue: string;
  isLoading: boolean;
  onSearch: (term: string) => void;
  requestTopicChange: (templateId: TopicBasicFragment['id']) => void;
  shouldAutoFocus: boolean;
  shouldValidate?: boolean;
  topicTitle: string;
};

/**
 * Topic selection for template compose modal
 *
 * @param props                    Props passed to the component
 * @param props.ariaDescribedBy    Accessible form description
 * @param props.ariaLabelledBy     Accessible form title
 * @param props.dataTopicAll       All available topics
 * @param props.dataTopicSaved     Currently selected topic
 * @param props.dataSearchValue    <input type="search" /> value attribute
 * @param props.isLoading          Whether the data is still being loaded
 * @param props.onSearch           Callback to invoke when search term is changed
 * @param props.requestTopicChange Request that selected topic is changed
 * @param props.shouldAutoFocus    Whether the input should be focused
 * @param props.shouldValidate     Whether the input should be validated
 * @param props.topicTitle         The title of the selected topic
 *
 * @returns                        Component itself
 */
const ComposeModalTopic: FC<Props> = ({
  ariaDescribedBy,
  ariaLabelledBy,
  dataTopicAll,
  dataTopicSaved,
  dataSearchValue,
  isLoading,
  onSearch,
  requestTopicChange,
  shouldAutoFocus,
  shouldValidate,
  topicTitle,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  /**
   * Select topic or unselect if it's already selected
   *
   * @param topic Topic id for selection
   */
  const requestTopicSelect = (topic: TopicBasicFragment['id']) => {
    requestTopicChange(topic);
    setAnchorEl(null);
  };

  const popoverHandlers: ExternalPopoverProps = {
    anchorEl,
    onStateChange: setAnchorEl,
  };

  return (
    <Popover
      aria-disabled={isLoading}
      externalHandlerProps={popoverHandlers}
      shouldHaveOffset={true}
      trigger={
        <ComposeTopicTrigger
          autoFocus={shouldAutoFocus}
          isOpen={Boolean(anchorEl)}
          shouldValidate={shouldValidate}
          value={topicTitle}
        />
      }
    >
      <TopicTemplateSelection
        ariaDescribedBy={ariaDescribedBy}
        ariaLabelledBy={ariaLabelledBy}
        dataAll={dataTopicAll}
        dataCurrent={dataTopicSaved}
        dataSearchValue={dataSearchValue}
        isLoading={isLoading}
        onSearch={onSearch}
        requestDataSelect={requestTopicSelect}
        type="topic"
      />
    </Popover>
  );
};

export default ComposeModalTopic;
